import { Box, Chip, Grid, Stack, styled, Tooltip, Typography, TypographyIcon } from 'ui';
import { CheckCircleOutlined, ErrorOutlineOutlined, InfoOutlined, WarningAmberOutlined } from 'ui/icons';

const itemVariants = {
  bold: { fontWeight: 'bold' },
  standard: { fontWeight: 'normal' },
};

const WarningIconBase = (props: React.ComponentProps<typeof WarningAmberOutlined>) => {
  return <WarningAmberOutlined data-testid="WarningAmberOutlinedIcon" {...props} />;
};

const WarningIcon = styled(WarningIconBase)(({ theme }) => ({
  color: theme.palette.warning.main,
  fontSize: '1.4rem',
}));

const ErrorStatusIconBase = (props: React.ComponentProps<typeof ErrorOutlineOutlined>) => {
  return <ErrorOutlineOutlined data-testid="ErrorOutlineOutlinedIcon" {...props} />;
};

const SuccessStatusIconBase = (props: React.ComponentProps<typeof CheckCircleOutlined>) => {
  return <CheckCircleOutlined data-testid="CheckCircleOutlinedIcon" {...props} />;
};

export const MaterialBreakdownItem = ({
  label,
  value,
  valueColor,
  showValueStatusIcon = false,
  secondaryValue,
  secondaryValueColor,
  variant = 'standard',
  tooltipContent,
  dataTestId,
  highlight,
  chipText,
  hideZeroValues = false,
  indentLevel = 0,
  showBorder = false,
  showUnderline,
}: {
  label: string;
  value?: string;
  valueColor?: 'success' | 'error';
  showValueStatusIcon?: boolean;
  secondaryValue?: string;
  secondaryValueColor?: 'success' | 'error';
  variant?: 'standard' | 'bold';
  tooltipContent?: string;
  dataTestId?: string;
  highlight?: boolean;
  chipText?: string;
  hideZeroValues?: boolean;
  indentLevel?: number;
  showUnderline?: boolean;
  showBorder?: boolean;
}) => {
  if (value === '0.0%' && hideZeroValues) {
    return null;
  }
  const { fontWeight } = itemVariants[variant];
  const StatusIcon = valueColor === 'error' ? ErrorStatusIconBase : SuccessStatusIconBase;
  const underlineOffset = 0.5 + indentLevel * 2;
  const shouldShowUnderline = !showBorder && (showUnderline ?? true);

  return (
    <Box
      sx={({ palette }) => ({
        backgroundColor: highlight ? palette.warning[100] : undefined,
        border: showBorder ? `1px solid ${palette.border.main}` : undefined,
        position: 'relative',
        py: 0.5,
        ...(shouldShowUnderline
          ? {
              '&::after': {
                backgroundColor: palette.border.main,
                bottom: 0,
                content: '""',
                height: '1px',
                left: `${underlineOffset * 8}px`,
                position: 'absolute',
                right: 0,
              },
            }
          : undefined),
      })}
      data-testid={dataTestId}
    >
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid size={secondaryValue || value ? 7 : 12}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              marginLeft: 0.5 + indentLevel * 2,
            }}
          >
            {highlight ? (
              <TypographyIcon icon={WarningIcon} variant="body2" noWrap fontWeight={fontWeight}>
                {label}
              </TypographyIcon>
            ) : (
              <Typography variant="body2" noWrap fontWeight={fontWeight}>
                {label}
              </Typography>
            )}
            {chipText ? (
              <Chip color="primary" label={chipText} size="small" data-testid="material-breakdown-item-chip" />
            ) : null}
            {tooltipContent ? (
              <Tooltip title={tooltipContent} placement="top">
                <InfoOutlined fontSize="small" data-testid="InfoOutlinedIcon" />
              </Tooltip>
            ) : null}
          </Stack>
        </Grid>
        {secondaryValue ? (
          <Grid size={2.5} data-testid="secondary-value">
            <Typography
              variant="body2"
              noWrap
              color={secondaryValueColor}
              fontWeight={fontWeight}
              sx={{
                textAlign: 'right',
              }}
            >
              {secondaryValue}
            </Typography>
          </Grid>
        ) : null}
        {value ? (
          <Grid size={secondaryValue ? 2.5 : 5}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {showValueStatusIcon && valueColor ? (
                <TypographyIcon
                  icon={StatusIcon}
                  variant="body2"
                  noWrap
                  color={valueColor}
                  fontWeight={fontWeight}
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  {value}
                </TypographyIcon>
              ) : (
                <Typography
                  variant="body2"
                  noWrap
                  color={valueColor}
                  fontWeight={fontWeight}
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  {value}
                </Typography>
              )}
            </Box>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};
